import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import logo from "../../static/images/logo.jpeg";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    const u = localStorage.getItem("savedUsername");
    const p = localStorage.getItem("savedPassword");
    const r = localStorage.getItem("rememberMe") === "true";
    if (r && u && p) {
      setUsername(u);
      setPassword(p);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (rememberMe) {
        localStorage.setItem("savedUsername", username);
        localStorage.setItem("savedPassword", password);
        localStorage.setItem("rememberMe", "true");
      } else {
        localStorage.clear();
      }

      const res = await login(username, password);
      
      // Get user role from response and redirect to appropriate dashboard
      const userRole = res?.role || res?.data?.role;
      
      // Role-based navigation
      switch (userRole) {
        case 'System Admin':
        case 'Admin':
          navigate("/admin-dashboard");
          break;
        case 'Documentation Manager':
          navigate("/documentation-manager-dashboard");
          break;
        case 'Survey Inspector':
          navigate("/survey-inspector-dashboard");
          break;
        case 'Client':
          navigate("/client-dashboard");
          break;
        case 'Accountant':
          navigate("/accountant-dashboard");
          break;
        default:
          // Default to admin dashboard if role is unknown
          navigate("/admin-dashboard");
          break;
      }
    } catch (err) {
      setError("Invalid username or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">

      {/* Back Button */}
      <Link
        to="/"
        className="absolute top-6 left-6 bg-orange text-white px-4 py-2 rounded shadow hover:bg-orange-600 transition"
      >
        ← Back to Home
      </Link>

      {/* Login Card */}
      <div className="w-full max-w-5xl bg-white shadow-2xl rounded overflow-hidden grid grid-cols-1 md:grid-cols-2">

        {/* LEFT SIDE */}
        <div className="flex flex-col justify-center items-center text-center px-10 py-12 bg-gray-50">
          <img src={logo} alt="uma" className="h-20 mb-6" />
          <h2 className="text-orange text-xl font-semibold mb-2">
            Manage with confidence
          </h2>
          <p className="text-gray-600 text-sm max-w-xs">
            Secure access to management
          </p>
        </div>

        {/* RIGHT SIDE */}
        <div className="px-10 py-12">
          <h1 className="text-2xl font-semibold text-orange mb-1">
            Sign in to your account
          </h1>
          <p className="text-sm text-gray-500 mb-6">
            Enter your credentials to continue
          </p>

          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-2 text-sm rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">

            {/* Username */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Username
              </label>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full border px-3 py-2 rounded focus:ring-2 focus:ring-orange-500"
                required
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border px-3 py-2 rounded pr-12 focus:ring-2 focus:ring-orange-500"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-orange"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Remember / Forgot */}
            <div className="flex justify-between items-center text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                Remember me
              </label>
              <Link to="/forgot-password" className="text-orange hover:underline">
                Forgot password?
              </Link>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-orange text-white py-2.5 rounded font-semibold hover:bg-orange-600 transition disabled:opacity-50"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>

      {/* Footer */}
      <div className="absolute bottom-4 text-xs text-gray-400">
        Designed with Passion by <span className="text-blue-600">DigiQin</span>
      </div>
    </div>
  );
};

export default Login;